import { Phone, PhoneMissed, PhoneOff, Video, VideoOff, PhoneOutgoing } from 'lucide-react';
import { formatDistanceToNowLocale } from '@/lib/date-locale';

interface CallEventBubbleProps {
  isVideo: boolean;
  duration?: number;
  isMissed: boolean;
  isOwn: boolean;
  /** Call status: 'missed', 'rejected', 'no-answer', 'completed', etc. */
  status?: string;
  /** Display name of the other party */
  callerName?: string;
  /** Timestamp of the call event */
  timestamp?: string;
  /** Tap to return call */
  onReturnCall?: (isVideo: boolean) => void;
}

export function CallEventBubble({
  isVideo,
  duration,
  isMissed,
  isOwn,
  status,
  callerName,
  timestamp,
  onReturnCall,
}: CallEventBubbleProps) {
  const formatDuration = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const isDeclined = status === 'rejected';

  const Icon = isMissed
    ? isDeclined
      ? PhoneOff
      : isVideo ? VideoOff : PhoneMissed
    : isVideo ? Video : Phone;

  const label = isMissed
    ? isDeclined
      ? 'Declined call'
      : isVideo ? 'Missed video call' : 'Missed voice call'
    : isVideo ? 'Video call' : 'Voice call';

  const accentClass = isMissed
    ? isDeclined
      ? 'text-warning'
      : 'text-destructive'
    : 'text-primary';

  const bgClass = isMissed
    ? isDeclined
      ? 'bg-warning/5 border-warning/20'
      : 'bg-destructive/5 border-destructive/20'
    : 'bg-primary/5 border-primary/20';

  const timeStr = timestamp
    ? formatDistanceToNowLocale(new Date(timestamp), { addSuffix: true })
    : undefined;

  return (
    <div className="flex items-center justify-center py-2">
      <button
        onClick={() => onReturnCall?.(isVideo)}
        disabled={!onReturnCall}
        className={`flex items-center gap-3 w-full max-w-[320px] px-4 py-3 rounded-2xl border text-left transition-all ${bgClass} ${
          onReturnCall ? 'cursor-pointer hover:shadow-md active:scale-[0.98]' : ''
        }`}
      >
        {/* Icon */}
        <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
          isMissed
            ? isDeclined ? 'bg-warning/10' : 'bg-destructive/10'
            : 'bg-muted/50'
        }`}>
          <Icon className={`h-5 w-5 ${accentClass}`} />
        </div>

        {/* Details */}
        <div className="flex-1 min-w-0">
          <p className={`text-[13px] font-semibold ${accentClass}`}>
            {label}
          </p>
          {callerName && (
            <p className="text-sm font-medium text-foreground truncate">
              {callerName}
            </p>
          )}
          <div className="flex items-center gap-2 mt-0.5">
            {timeStr && (
              <span className="text-[11px] text-muted-foreground">
                {timeStr}
              </span>
            )}
            {duration && duration > 0 && (
              <span className="text-[11px] text-muted-foreground">
                • {formatDuration(duration)}
              </span>
            )}
          </div>
        </div>

        {/* Return call hint - show for all calls when onReturnCall is available */}
        {onReturnCall && (
          <div className="flex-shrink-0">
            <PhoneOutgoing className={`h-5 w-5 ${accentClass} opacity-60`} />
          </div>
        )}
      </button>
    </div>
  );
}
